/**
 * Colour-coded status badge component per DR-007.
 * Maps status values to colour variants with aria-label.
 */

import { cn } from "@/lib/utils";

type BadgeVariant =
  | "green"
  | "danger"
  | "warning"
  | "info"
  | "muted"
  | "purple";

const VARIANT_STYLES: Record<BadgeVariant, string> = {
  green: "bg-sc-success-bg text-sc-success",
  danger: "bg-red-100 text-sc-danger dark:bg-red-900/30",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  info: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  muted: "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400",
  purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

// PIM Session status -> variant
const PIM_STATUS_MAP: Record<string, BadgeVariant> = {
  Active: "green",
  Activating: "info",
  Expired: "muted",
  Revoked: "danger",
  Revoking: "warning",
  RevocationPending: "warning",
  Failed: "danger",
  Deactivating: "warning",
  PendingApproval: "purple",
  ApprovalExpired: "muted",
};

// GDAP Relationship status -> variant
const GDAP_STATUS_MAP: Record<string, BadgeVariant> = {
  Active: "green",
  Approved: "info",
  Approving: "info",
  Creating: "info",
  Created: "muted",
  CreationFailed: "danger",
  Terminated: "danger",
  Terminating: "warning",
  Expired: "muted",
};

// Service Tier -> variant
const TIER_MAP: Record<string, BadgeVariant> = {
  Enterprise: "purple",
  Premium: "info",
  Standard: "green",
  Essential: "warning",
  Monitoring: "muted",
};

// Technician Tier -> variant (Tier-0 highest privilege = red, Tier-4 lowest = green)
const TECH_TIER_MAP: Record<string, BadgeVariant> = {
  "Tier-0": "danger",
  "Tier-1": "warning",
  "Tier-2": "info",
  "Tier-3": "purple",
  "Tier-4": "green",
};

// Severity -> variant
const SEVERITY_MAP: Record<string, BadgeVariant> = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "danger",
};

// Access type -> variant
const ACCESS_TYPE_MAP: Record<string, BadgeVariant> = {
  OPEN: "green",
  LOCKED: "warning",
};

interface StatusBadgeProps {
  readonly status: string;
  readonly category?:
    | "pim"
    | "gdap"
    | "tier"
    | "techTier"
    | "severity"
    | "accessType";
  readonly className?: string;
}

export function StatusBadge({
  status,
  category,
  className,
}: StatusBadgeProps) {
  let variant: BadgeVariant = "muted";

  switch (category) {
    case "pim":
      variant = PIM_STATUS_MAP[status] ?? "muted";
      break;
    case "gdap":
      variant = GDAP_STATUS_MAP[status] ?? "muted";
      break;
    case "tier":
      variant = TIER_MAP[status] ?? "muted";
      break;
    case "techTier":
      variant = TECH_TIER_MAP[status] ?? "muted";
      break;
    case "severity":
      variant = SEVERITY_MAP[status] ?? "muted";
      break;
    case "accessType":
      variant = ACCESS_TYPE_MAP[status] ?? "muted";
      break;
    default:
      variant = "muted";
  }

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium",
        VARIANT_STYLES[variant],
        variant === "info" && status === "Activating" && "animate-sc-pulse",
        className,
      )}
      aria-label={`Status: ${status}`}
    >
      {status}
    </span>
  );
}
